import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ApplicationsApiService } from 'src/app/services/api/applications-api.service';
import { parseLocaleNumber } from 'src/app/utils/format-currency';
import { LocaleService } from 'src/app/services/locale.service';
import { ApplicationService } from 'src/app/services/application-service';
import { handleApiError } from 'src/app/utils/error';
import { Subscription, timer } from 'rxjs';
import { FlowService } from 'src/app/services/flow.service';
import { TrackingService } from 'src/app/services/tracking.service';

@Component({
  selector: 'app-monthly-sales',
  templateUrl: './monthly-sales.component.html',
  styleUrls: ['./monthly-sales.component.scss'],
})
export class MonthlySalesComponent implements OnInit, OnDestroy {
  applicationId!: number;
  monthlySalesForm!: FormGroup;
  error!: string;
  estimatedTurnoverCurrency!: any;
  locale!: string;
  subs: Array<Subscription> = [];
  requestPending = false;
  @Input() isSummary!: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private applicationService: ApplicationService,
    private applicationsApiService: ApplicationsApiService,
    private localeService: LocaleService,
    public flowService: FlowService,
    protected trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.applicationId = this.applicationService.applicationId;
    this.locale = this.localeService.locale;

    const initialMonthlySales = this.applicationService.estimatedMonthlySales;

    this.monthlySalesForm = this.formBuilder.group({
      monthlySales: [
        initialMonthlySales,
        {
          updateOn: 'blur',
          validators: [Validators.required],
        },
      ],
    });

    this.subs.push(
      this.monthlySales.valueChanges.subscribe(
        (v) => {
          if (this.checkTypeAndLength('string', v)) {
            v = parseLocaleNumber(v, this.locale);
            this.applicationService.estimatedMonthlySales = v;
            const turnoverPrev = v * 12;
            this.applicationService.estimatedTurnoverPrev = turnoverPrev;
            if (this.isSummary) {
              this.applicationsApiService
                .updateApplication({
                  estimatedMonthlySales: v,
                  estimatedTurnoverPrev: turnoverPrev,
                  applicationId: this.applicationId,
                })
                .pipe(take(1))
                .subscribe(
                  () => {
                    this.error = '';
                    this.trackFields('monthlySalesUpdated');
                  },
                  (error: any) => handleApiError(error, this.translateService)
                );
            } else {
              this.error = '';
              this.trackFields('monthlySales');
            }
          }
        },
        (error: any) => handleApiError(error, this.translateService)
      )
    );
  }

  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }

  handleNextButtonClick(): void {
    this.subs.push(
      timer(1000).subscribe(() => {
        if (!this.form.invalid) {
          this.flowService.nextScreen();
        }
      })
    );
  }

  get form(): FormGroup {
    return this.monthlySalesForm;
  }

  get monthlySales(): FormControl {
    return this.form.get('monthlySales') as FormControl;
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  checkTypeAndLength(type: string, value: string): boolean {
    return typeof value === type && value.length ? true : false;
  }

  getErrors(control: string): string | null {
    const monthlySales = this.monthlySales;

    switch (control) {
      case 'monthlySales':
        if (monthlySales.hasError('required')) {
          return this.translateService.instant(
            'global.error.monthlySalesRequired'
          );
        }
        return null;
      default:
        return null;
    }
  }
}
